import { useCallback } from 'react'
import {
  getProjectAutofillDraft,
  postProjectAutofillDraftFile
} from '../../../../../../../services/projectAutofillDraftService.ts'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotsHavePages,
  extractDraftMetadataFilesFromResponse,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizePageList,
  pickNewFileIdAfterUpload
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'

type SaveState = 'idle' | 'ok' | 'error'

export function useProjectAutofillRubroDocumentation(args: {
  draftId: string | undefined
  metadataFiles: DraftMetadataFileEntry[]
  setRubros: React.Dispatch<React.SetStateAction<RubroRow[]>>
  setMetadataFilesOverride: React.Dispatch<React.SetStateAction<DraftMetadataFileEntry[] | null>>
  setUploadingSlotKey: React.Dispatch<React.SetStateAction<string | null>>
  setSaveState: React.Dispatch<React.SetStateAction<SaveState>>
}) {
  const { draftId, metadataFiles, setRubros, setMetadataFilesOverride, setUploadingSlotKey, setSaveState } = args

  const applyUploadedFilesList = useCallback(
    async (res: unknown): Promise<DraftMetadataFileEntry[] | null> => {
      let next = extractDraftMetadataFilesFromResponse(res)
      if ((!next || next.length === 0) && draftId) {
        try {
          const fresh = await getProjectAutofillDraft(String(draftId))
          next = extractDraftMetadataFilesFromResponse(fresh) ?? null
        } catch {
        }
      }
      if (next && next.length > 0) {
        setMetadataFilesOverride(next)
        return next
      }
      return null
    },
    [draftId, setMetadataFilesOverride]
  )

  const handleSelectRubroSlotFileId = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, fileId: string | null) => {
      setRubros((prev) =>
        prev.map((r) => {
          if (r.tempId !== tempId) return r
          const cur = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
          const next = { ...cur }
          if (fileId && fileId.trim()) {
            next[slot] = fileId.trim()
            const slotsCur = normalizeDocumentationSlots(r.documentationSlots ?? {})
            const nextSlots: DocumentationSlots = { ...slotsCur }
            delete nextSlots[slot]
            const hasAnySlots = documentationSlotsHavePages(nextSlots)
            const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
            return {
              ...r,
              documentationFileIds: hasAny ? next : undefined,
              documentationSlots: hasAnySlots ? nextSlots : {}
            }
          }
          delete next[slot]
          const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
          return { ...r, documentationFileIds: hasAny ? next : undefined }
        })
      )
      setSaveState('idle')
    },
    [setRubros, setSaveState]
  )

  const handleRubroSlotPagesChange = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, pages: number[]) => {
      setRubros((prev) =>
        prev.map((r) => {
          if (r.tempId !== tempId) return r
          const cur = normalizeDocumentationSlots(r.documentationSlots ?? {})
          const norm = normalizePageList(pages)
          const nextSlots: DocumentationSlots = { ...cur }
          if (norm?.length) nextSlots[slot] = norm
          else delete nextSlots[slot]
          const hasAny = documentationSlotsHavePages(nextSlots)
          return { ...r, documentationSlots: hasAny ? nextSlots : {} }
        })
      )
      setSaveState('idle')
    },
    [setRubros, setSaveState]
  )

  const handleUploadRubroSlotFile = useCallback(
    async (tempId: string, slot: keyof DocumentationSlots, file: File) => {
      if (!draftId) return
      const key = `${tempId}:${slot}`
      setUploadingSlotKey(key)
      setSaveState('idle')
      try {
        const prevList = metadataFiles
        const res = await postProjectAutofillDraftFile(String(draftId), file)
        const next = await applyUploadedFilesList(res)
        if (next?.length) {
          const newId = pickNewFileIdAfterUpload(prevList, next)
          if (newId) {
            handleSelectRubroSlotFileId(tempId, slot, newId)
          }
        }
      } catch {
        setSaveState('error')
      } finally {
        setUploadingSlotKey(null)
      }
    },
    [
      draftId,
      metadataFiles,
      applyUploadedFilesList,
      handleSelectRubroSlotFileId,
      setUploadingSlotKey,
      setSaveState
    ]
  )

  return {
    applyUploadedFilesList,
    handleSelectRubroSlotFileId,
    handleRubroSlotPagesChange,
    handleUploadRubroSlotFile
  }
}
